import React, { useEffect, useMemo, useState } from "react";
import InlineLoading from "./InlineLoading";

const getIssueKey = (issue, index) => (
  issue?.varietyPriceId ? String(issue.varietyPriceId) : `issue-${index}`
);

const isFixableEquivalenceIssue = (issue) => (
  issue?.type === "missing_inventory_equivalence" && issue?.varietyPriceId
);

export const buildInventoryReadinessPayload = ({
  canUpdateProduct,
  branchId,
  fixableIssues,
  values,
}) => {
  if (!canUpdateProduct) throw new Error("No tienes permisos para editar productos.");
  if (!branchId) throw new Error("Selecciona una sucursal para guardar equivalencias.");
  const payload = fixableIssues.map((issue, index) => {
    const key = getIssueKey(issue, index);
    const rawValue = String(values[key] || "").trim();
    const numericValue = Number(rawValue);
    if (!rawValue || !Number.isFinite(numericValue) || numericValue <= 0) {
      throw new Error("Completa todas las equivalencias con un número mayor a 0.");
    }
    return {
      varietyPriceId: issue.varietyPriceId,
      baseQuantityPerUnit: numericValue,
    };
  });
  if (!payload.length) {
    throw new Error("No hay equivalencias que se puedan corregir desde este modal.");
  }
  return payload;
};

const CategoryInventoryReadinessModal = ({
  isOpen,
  failure,
  branchId,
  canUpdateProduct,
  isSaving,
  onClose,
  onSaveAll,
}) => {
  const issues = useMemo(() => failure?.details?.issues || [], [failure]);
  const fixableIssues = useMemo(
    () => issues.filter(isFixableEquivalenceIssue),
    [issues]
  );
  const blockedIssues = useMemo(
    () => issues.filter((issue) => !isFixableEquivalenceIssue(issue)),
    [issues]
  );
  const [values, setValues] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    const nextValues = {};
    fixableIssues.forEach((issue, index) => {
      nextValues[getIssueKey(issue, index)] = "";
    });
    setValues(nextValues);
    setError("");
  }, [fixableIssues, isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    try {
      const payload = buildInventoryReadinessPayload({
        canUpdateProduct,
        branchId,
        fixableIssues,
        values,
      });
      setError("");
      await onSaveAll(payload);
    } catch (err) {
      setError(err?.message || "No se pudieron guardar las equivalencias.");
    }
  };

  return (
    <div className="fixed inset-0 z-[1800] flex items-start justify-center overflow-y-auto bg-black/60 px-4 py-8">
      <div className="w-full max-w-3xl overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-100 bg-amber-50 px-6 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
              Inventario pendiente
            </p>
            <h2 className="text-lg font-semibold text-gray-900">
              Completar equivalencias
            </h2>
            <p className="mt-1 text-sm text-gray-700">
              {failure?.message || "La categoría tiene formatos incompletos."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-amber-200 bg-white text-gray-600 hover:text-gray-900"
            disabled={isSaving}
          >
            x
          </button>
        </div>

        <div className="space-y-4 p-6">
          {fixableIssues.length > 0 && (
            <div className="space-y-3">
              {fixableIssues.map((issue, index) => {
                const key = getIssueKey(issue, index);
                return (
                  <div key={key} className="rounded-lg border border-amber-200 bg-amber-50/70 p-4">
                    <p className="text-sm font-semibold text-gray-900">
                      {issue.message}
                    </p>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_180px] sm:items-end">
                      <div className="text-xs text-gray-600">
                        <span className="font-semibold">Formato:</span> #{issue.varietyPriceId}
                        <span className="mx-2">·</span>
                        <span className="font-semibold">Variedad:</span> #{issue.varietyId}
                      </div>
                      <label className="text-xs font-semibold uppercase text-gray-600">
                        1 {issue.metricUnit || "unidad"} =
                        <div className="mt-1 flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            step="0.000001"
                            value={values[key] || ""}
                            onChange={(event) =>
                              setValues((prev) => ({ ...prev, [key]: event.target.value }))
                            }
                            className="h-10 w-full rounded border border-amber-300 px-3 text-sm normal-case"
                            placeholder="Ej: 1.5"
                            disabled={isSaving || !canUpdateProduct}
                          />
                          <span className="whitespace-nowrap text-sm font-semibold normal-case text-gray-700">
                            {issue.baseUnit || "base"}
                          </span>
                        </div>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {blockedIssues.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-semibold text-red-800">
                Estos problemas requieren editar el formato del producto:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-red-700">
                {blockedIssues.map((issue, index) => (
                  <li key={getIssueKey(issue, index)}>{issue.message}</li>
                ))}
              </ul>
            </div>
          )}

          {!canUpdateProduct && (
            <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
              Puedes revisar los formatos pendientes, pero necesitas permiso de edición de productos para corregirlos.
            </p>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex flex-col gap-2 border-t border-gray-100 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-gray-500">
            Al guardar, se reintentará activar inventario en la categoría.
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:text-gray-900 disabled:opacity-60"
              onClick={onClose}
              disabled={isSaving}
            >
              Cerrar
            </button>
            <button
              type="button"
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              onClick={handleSave}
              disabled={isSaving || !canUpdateProduct || fixableIssues.length === 0}
            >
              {isSaving ? <InlineLoading label="Guardando" /> : "Guardar y activar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryInventoryReadinessModal;
